import {
  Box,
  Button,
  Col,
  Grid,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  NumberInput,
  Textarea,
  Flex,
  Image,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { notifications } from 'notifications';
import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLoginUser } from 'redux/selectors/useLoginUser';
import { API } from 'services';
import { GradientButton } from 'components/GradientButton';
import loading from 'assets/report/loading.gif';

export function TimesheetEnd() {
  const navigate = useNavigate();
  const { loginUser } = useLoginUser();
  const [searchParams] = useSearchParams();
  const [isLoading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isDateAllowed] = useState(true);

  const timesheetId = searchParams.get('timesheet_id');

  const form = useForm({
    initialValues: {
      date: new Date(),
      course_name: '',
      course_run_id: '',
      no_of_assessment: 0,
      sign_out_coa: '',
      no_of_hours_worked: 0,
      remarks: '',
    },
    validate: (values) => {
      const errors: any = {};
      return errors;
    },
  });

  const timesheetQuery = useQuery(
    ['timesheet', timesheetId],
    () => API.getTimesheetDetails({ timesheet_id: timesheetId || '' }).then((res) => res.data),
    {
      enabled: !!timesheetId,
    }
  );

  // Populate form with existing data when editing
  useEffect(() => {
    if (timesheetQuery.data?.TrainerTimesheet) {
      const timesheetData = timesheetQuery.data.TrainerTimesheet;
      form.setValues({
        date: new Date(timesheetData.trainertimesheet_date),
        course_name: timesheetData.course_title?.toString() || '',
        course_run_id: timesheetData.courserun_course_from
          ? timesheetData.courserun_course_from.toString() +
            ' - ' +
            timesheetData.courserun_course_to?.toString()
          : '',
        no_of_assessment: timesheetData.trainertimesheet_assessment_count || 0,
        sign_out_coa: timesheetData.trainertimesheet_sign_out_coa || '',
        no_of_hours_worked: parseFloat(timesheetData.trainertimesheet_working_hrs) || 0,
        remarks: timesheetData.trainertimesheet_remark || '',
      });
      setSelectedDate(new Date(timesheetData.trainertimesheet_date));

      // If sign_out_coa is not null, auto-fill with current datetime
      if (!timesheetData.trainertimesheet_sign_out_coa) {
        const now = new Date();
        const formatted = now.toLocaleString('en-GB', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
        form.setFieldValue('sign_out_coa', formatted);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheetQuery.data]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const timesheetData = {
        ...values,
        date: selectedDate.toISOString().split('T')[0], // Format date as YYYY-MM-DD
        trainer_id: loginUser.trainer_id,
      };

      const response = await API.endTimesheet({
        ...timesheetData,
        timesheet_id: timesheetId,
      });

      if (response.data.status) {
        notifications.success({
          title: 'Timesheet end successfully',
          message: '',
        });
        navigate('/timesheets');
      } else {
        notifications.error({
          title: 'Operation failed',
          message: response.data.message || 'Please try again',
        });
      }
    } catch (error) {
      notifications.error({
        title: 'Operation failed',
        message: 'Please try again',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack px={{ base: 0, md: 30 }}>
      {(isLoading || timesheetQuery.isLoading) && (
        <Box className={'loading_box'}>
          <Image src={loading} width="100"></Image>
        </Box>
      )}

      {!isLoading &&
        timesheetQuery &&
        timesheetQuery.data &&
        timesheetQuery.data.TrainerTimesheet && (
          <Flex className={'flex_wrap'}>
            <Box className={'w100 md_w100'}>
              <Box bg="#FFF" p={20}>
                <Box
                  mb={20}
                  className="flex flex_direction_row flex_align_center flex_justify_between"
                >
                  <Title order={2}>
                    Timesheet Sign Out{' '}
                    {timesheetQuery.data.TrainerTimesheet.trainertimesheet_no
                      ? ` - ${timesheetQuery.data.TrainerTimesheet.trainertimesheet_no}`
                      : ''}
                  </Title>
                </Box>
                <Box>
                  <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Grid>
                      <Col span={12} sm={6}>
                        <Text className="invoice_date_title">Date</Text>
                        <DatePicker
                          selected={selectedDate}
                          onChange={(date: any) => null}
                          dateFormat="dd/MM/yyyy"
                          wrapperClassName={`custom-datepicker-wrapper disabled`}
                          disabled={true}
                          readOnly={true}
                        />
                      </Col>
                      <Col span={12} sm={6}>
                        <TextInput
                          label="Sign Out (COA)"
                          placeholder="Enter sign out COA"
                          readOnly
                          disabled
                          {...form.getInputProps('sign_out_coa')}
                        />
                      </Col>
                      <Col span={12} sm={6}>
                        <TextInput
                          label="Course Name"
                          placeholder="Enter course name"
                          readOnly
                          disabled
                          {...form.getInputProps('course_name')}
                        />
                      </Col>
                      <Col span={12} sm={6}>
                        <TextInput
                          label="Course Run"
                          placeholder="Enter course run"
                          readOnly
                          disabled
                          {...form.getInputProps('course_run_id')}
                        />
                      </Col>
                      <Col span={12} sm={6}>
                        <NumberInput
                          label="No of Assessment"
                          placeholder="Enter number of assessments"
                          min={0}
                          {...form.getInputProps('no_of_assessment')}
                        />
                      </Col>
                      <Col span={12} sm={6}>
                        <NumberInput
                          label="No of Hours Worked"
                          placeholder="Enter hours worked"
                          min={0}
                          precision={1}
                          step={0.5}
                          {...form.getInputProps('no_of_hours_worked')}
                        />
                      </Col>
                      <Col span={12}>
                        <Textarea
                          label="Remarks"
                          placeholder="Enter any additional remarks"
                          {...form.getInputProps('remarks')}
                        />
                      </Col>
                    </Grid>
                    <hr
                      style={{ borderTop: '1px solid', borderColor: '#ddd', marginTop: '20px' }}
                    ></hr>
                    <Group position="right" mt="xl">
                      <Button
                        variant="outline"
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={() => navigate('/timesheets')}
                      >
                        Cancel
                      </Button>
                      <GradientButton type="submit" loading={isLoading} disabled={!isDateAllowed}>
                        {timesheetQuery.data.TrainerTimesheet.trainertimesheet_sign_out_coa
                          ? 'Update Timesheet'
                          : 'Sign Out Timesheet'}
                      </GradientButton>
                    </Group>
                  </form>
                </Box>
              </Box>
            </Box>
          </Flex>
        )}
    </Stack>
  );
}
